import { Box, CircularProgress, Stack, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  EMPTY_NEWS_FORM,
  NewsItemAdmin,
  NewsItemFormValues,
  newsItemToForm,
} from '../../../models/NewsItem';
import { Palette } from 'Palette';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../../components/styles/StyledButtons';
import StyledModal from '../../../components/styles/StyledModal';
import { StyledTypography } from '../../../components/styles/styles';
import NewsCardPreview from './NewsCardPreview';

type Props = {
  open: boolean;
  editing: NewsItemAdmin | null;
  onClose: () => void;
  onSubmit: (values: NewsItemFormValues) => Promise<void>;
  onRefreshFromSource: (id: string) => Promise<NewsItemAdmin | null>;
  onPublish: (item: NewsItemAdmin) => Promise<void>;
};

export default function NewsAdminFormModal({
  open,
  editing,
  onClose,
  onSubmit,
  onRefreshFromSource,
  onPublish,
}: Props) {
  const [values, setValues] = useState<NewsItemFormValues>(EMPTY_NEWS_FORM);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues(editing ? newsItemToForm(editing) : EMPTY_NEWS_FORM);
  }, [open, editing]);

  const set =
    (field: keyof NewsItemFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValues((v) => ({ ...v, [field]: e.target.value }));

  const canSave =
    values.title.trim() !== '' &&
    values.summary.trim() !== '' &&
    values.canonicalUrl.trim() !== '' &&
    values.publishDate !== '';

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    if (!editing) return;
    setRefreshing(true);
    try {
      const updated = await onRefreshFromSource(editing.id);
      if (updated) setValues(newsItemToForm(updated));
    } finally {
      setRefreshing(false);
    }
  };

  const handlePublish = async () => {
    if (!canSave || !editing) return;
    setPublishing(true);
    try {
      await onSubmit(values);
      await onPublish(editing);
    } finally {
      setPublishing(false);
    }
  };

  const isWebsiteSourced = !!editing && editing.sourceType !== 'manual';
  const isDraft = editing?.status === 'draft';
  const busy = saving || publishing;

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit news item' : 'Add news item'}
      maxWidth="lg"
      fullWidth
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          px: 3,
          pb: 1,
          alignItems: 'flex-start',
        }}
      >
        {/* Editor */}
        <Stack
          spacing={2}
          sx={{ flex: 1, minWidth: 320, maxHeight: '68vh', overflowY: 'auto', pr: 1 }}
        >
          {isWebsiteSourced && (
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={16} /> : undefined}
              sx={{ ...SECONDARY_BUTTON_HOVER_SX, alignSelf: 'flex-start' }}
            >
              Refresh from website
            </StyledButton>
          )}
          <TextField label="Title" size="small" value={values.title} onChange={set('title')} />
          <TextField
            label="Publish date"
            type="date"
            size="small"
            value={values.publishDate}
            onChange={set('publishDate')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Minutes to read"
            type="number"
            size="small"
            value={values.minutesToRead}
            onChange={set('minutesToRead')}
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            label="Short summary"
            size="small"
            value={values.summary}
            onChange={set('summary')}
            multiline
            minRows={3}
          />
          <TextField
            label="Publisher name (not shown in app)"
            size="small"
            value={values.publisherName}
            onChange={set('publisherName')}
          />
          <TextField
            label="Publisher avatar URL (not shown in app)"
            size="small"
            value={values.publisherAvatarUrl}
            onChange={set('publisherAvatarUrl')}
          />
          <TextField
            label="Article URL"
            size="small"
            value={values.canonicalUrl}
            onChange={set('canonicalUrl')}
          />
          <TextField
            label="Image URL (optional)"
            size="small"
            value={values.imageUrl}
            onChange={set('imageUrl')}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              sx={SECONDARY_BUTTON_HOVER_SX}
              onClick={onClose}
            >
              Cancel
            </StyledButton>
            <StyledButton
              noTopSpacing
              buttonVariant={isDraft ? 'inactiveOrCancel' : 'default'}
              sx={isDraft ? SECONDARY_BUTTON_HOVER_SX : undefined}
              onClick={() => void handleSave()}
              disabled={!canSave || busy}
            >
              {!editing ? 'Add draft' : isDraft ? 'Save draft' : 'Save'}
            </StyledButton>
            {isDraft && (
              <StyledButton
                noTopSpacing
                onClick={() => void handlePublish()}
                disabled={!canSave || busy}
              >
                Publish
              </StyledButton>
            )}
          </Stack>
        </Stack>

        {/* Live app preview */}
        <Box
          sx={{
            flexShrink: 0,
            position: { md: 'sticky' },
            top: 0,
            alignSelf: { xs: 'center', md: 'flex-start' },
          }}
        >
          <StyledTypography
            variant="caption"
            sx={{
              color: Palette.default.disabledText,
              display: 'block',
              mb: 0.5,
              textAlign: 'center',
            }}
          >
            App preview
          </StyledTypography>
          <NewsCardPreview values={values} />
        </Box>
      </Box>
    </StyledModal>
  );
}
